import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Stack,
  Box,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import { RefreshRounded, CheckCircle } from '@mui/icons-material';
import PlanChip from '../../PlanChip';
import ProfileWithPlanBadge from '../../ProfileWithPlanBadge';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getYourData } from '../../../utils/yourDataStore';
import { useDefaultProfile } from '../../../contexts/DefaultProfileContext';
import { usePermissions } from '../../../contexts/PermissionContext';
import logger from '../../../utils/logger';
import ApiService from '../../../services/api';
import { useProfilePlan } from '../../../contexts/ProfilePlanContext';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  overflow: 'hidden',
  background: 'white',
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const MobileCardsContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  }
}));

const MobileBusinessCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
    transform: 'translateY(-4px)'
  }
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: '#0b91d6',
  '& .MuiTableCell-head': {
    background: 'transparent',
    color: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    padding: '20px 24px',
    border: 'none',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8f9ff',
    transform: 'scale(1.01)'
  },
  '&:not(:last-child)': {
    borderBottom: '1px solid #f0f0f0',
  },
  '& .MuiTableCell-body': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    padding: '20px 24px',
    border: 'none',
  },
}));

const BusinessName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#30302E',
  marginBottom: '4px',
}));

const BusinessAddress = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: '#30302E',
}));

const OptimizationScore = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#30302E',
}));

const ViewProfileButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#0B91D6',
  textTransform: 'none',
  padding: '4px 8px',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
}));

const CheckNowButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  padding: '6px 2px',
  width: '100px',
  height: '30px',
  borderRadius: '6px',
  color: '#ffffff',
  backgroundColor: '#0B91D6',
  '&:hover': {
    backgroundColor: '#0a7db7',
  },
}));

const StatusBadge = styled(Box)(({ status }) => ({
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'capitalize',
  backgroundColor:
    status === 'verified'
      ? '#E6F4EA'
      : status === 'pending_verification'
        ? '#FFF4E5'
        : status === 'suspended'
          ? '#FDECEA'
          : status === 'unverified'
            ? '#FBE9E9'
            : '#F0F0F0',
  color:
    status === 'verified'
      ? '#1E4620'
      : status === 'pending_verification'
        ? '#8A6D3B'
        : status === 'suspended'
          ? '#A94442'
          : status === 'unverified'
            ? '#D32F2F'
            : '#444',
}));

const RefreshIconButton = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  marginLeft: '8px',
  color: '#0B91D6',
  '&:hover': {
    backgroundColor: 'rgba(11, 145, 214, 0.1)',
    color: '#0a7db7',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
  },
}));

const ScoreContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const SelectDefaultButton = styled(Button)(({ theme, isdefault }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  padding: '6px 12px',
  borderRadius: '6px',
  minWidth: '140px',
  border: isdefault === 'true' ? '2px solid #4CAF50' : '1px solid #0B91D6',
  color: isdefault === 'true' ? '#4CAF50' : '#0B91D6',
  backgroundColor: isdefault === 'true' ? '#E8F5E9' : 'transparent',
  '&:hover': {
    backgroundColor: isdefault === 'true' ? '#C8E6C9' : 'rgba(11, 145, 214, 0.08)',
  },
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
}));

const LabelText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  color: '#6B7280',
  marginBottom: '4px',
}));

const getPlanColor = (plan) => {
  const colors = {
    basic: '#10B981',
    advance: '#3B82F6',
    pro: '#8B5CF6'
  };
  return colors[String(plan)?.toLowerCase()] || '#64748B';
};

// Use shared PlanChip component for consistent badge style

const PlanCellBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
}));

const PlanBadge = styled(Box)(({ theme, bgcolor }) => ({
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: 700,
  backgroundColor: bgcolor || '#F1F5F9',
  color: '#fff'
}));

const StatusDot = styled(Box)(({ color }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: color || '#9CA3AF',
  boxShadow: '0 0 0 2px rgba(0,0,0,0.04)'
}));

const BusinessTable = ({ businesses = [], subscriptions = {} }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { defaultProfile, setDefaultProfile } = useDefaultProfile();
  const { isAdmin, hasFullAccess } = usePermissions();

  const [profileSnapshot, setProfileSnapshot] = React.useState(() => {
    const store = getYourData();
    return store?.profileStrength || null;
  });

  // Get default profile ID from context
  const defaultProfileId = defaultProfile?.locationId || defaultProfile?.id;

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [planByGmb, setPlanByGmb] = React.useState({});
  const { loadProfileSubscription } = useProfilePlan();

  React.useEffect(() => {
    const refresh = () => {
      try {
        const store = getYourData();
        setProfileSnapshot(store?.profileStrength || null);
      } catch { }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    refresh();
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  // Fetch plan info for businesses which don't already have a plan using ProfilePlanContext
  React.useEffect(() => {
    let mounted = true;
    const missingProfiles = (businesses || []).filter(b => {
      const locId = b.locationId || b.id;
      const hasSubPlan = subscriptions && subscriptions[locId] && (subscriptions[locId].plan || subscriptions[locId].product?.plan);
      const hasBusinessPlan = b.plan;
      const hasCachedPlan = planByGmb[String(locId)];
      return locId && !hasSubPlan && !hasBusinessPlan && !hasCachedPlan;
    }).map(b => {
      const locId = b.locationId || b.id;
      // Build profileKey as the subscription service expects (account:location or location id)
      // Try to use accountId if present for better matching
      if (b.accountId && locId) return `${b.accountId}:${locId}`;
      return String(locId);
    });

    if (missingProfiles.length === 0) return;

    (async () => {
      try {
        const results = {};
        await Promise.all(missingProfiles.map(async (profileKey) => {
          try {
            const resp = await loadProfileSubscription(profileKey).catch(() => null);
            const plan = resp?.planCode || resp?.plan || (resp?.data && resp.data.plan) || null;
            if (mounted) {
              // Derive locId key to store result (use last segment after ':' if present)
              const locKey = String(profileKey).includes(':') ? String(profileKey).split(':').pop() : String(profileKey);
              results[locKey] = plan;
            }
          } catch (e) {
            if (mounted) {
              const locKey = String(profileKey).includes(':') ? String(profileKey).split(':').pop() : String(profileKey);
              results[locKey] = null;
            }
          }
        }));

        if (mounted) setPlanByGmb(prev => ({ ...prev, ...results }));
      } catch (err) {
        logger.warn('Failed to fetch plans for GMBs via ProfilePlanContext', err);
      }
    })();

    return () => { mounted = false; };
  }, [businesses, subscriptions, loadProfileSubscription]);

  const handleViewProfile = (business) => {
    const locationId = business.locationId || business.id || '112694470912208695';
    navigate(`/business-profile?id=${locationId}`);
  };

  const handleCheckNow = (business) => {
    try {
      if (!isAdmin && !hasFullAccess) {
        const token = window.localStorage.getItem('came_from_trial') || (document.cookie.match(/(?:^|; )came_from_trial=([^;]+)/) || [])[1];
        const sig = window.localStorage.getItem('came_from_trial_sig') || (document.cookie.match(/(?:^|; )came_from_trial_sig=([^;]+)/) || [])[1];
        if (token) {
          let qs = `trial=true&trialToken=${encodeURIComponent(token)}`;
          if (sig) qs += `&sig=${encodeURIComponent(sig)}`;
          navigate(`/trial-landing?${qs}`);
          return;
        }
      }
    } catch (e) { }
    navigate('/profile-strength-analysis', { state: { businesses, selected: business } });
  };

  const handleRefreshScore = (business) => {
    try {
      if (!isAdmin && !hasFullAccess) {
        const token = window.localStorage.getItem('came_from_trial') || (document.cookie.match(/(?:^|; )came_from_trial=([^;]+)/) || [])[1];
        const sig = window.localStorage.getItem('came_from_trial_sig') || (document.cookie.match(/(?:^|; )came_from_trial_sig=([^;]+)/) || [])[1];
        if (token) {
          let qs = `trial=true&trialToken=${encodeURIComponent(token)}`;
          if (sig) qs += `&sig=${encodeURIComponent(sig)}`;
          navigate(`/trial-landing?${qs}`);
          return;
        }
      }
    } catch (e) { }
    navigate('/profile-strength-analysis', { state: { businesses, selected: business } });
  };

  const handleSelectDefaultProfile = async (business) => {
    try {
      logger.log('[BusinessTable] Selecting default profile, full business object:', business);

      const locationId = business.locationId || business.id;
      const accountId = business.accountId || business.account?.name?.split('/').pop();

      logger.log('[BusinessTable] Extracted IDs:', {
        locationId,
        accountId,
        locationDataExists: !!business.locationData,
        accountExists: !!business.account,
        locationDataName: business.locationData?.name,
        accountName: business.account?.name
      });

      // Note: Validation is done at the backend when actually posting
      // If this location doesn't exist, the error message will guide the user
      logger.log('[BusinessTable] Selected location will be validated when posting');

      // Create GMB account object
      const gmbAccount = {
        name: business.account?.name || `accounts/${accountId}`,
        accountName: business.account?.accountName || business.name,
        type: business.accountType || 'PERSONAL'
      };

      // Create GMB location object - use the FULL locationData from the API
      const gmbLocation = business.locationData ? {
        ...business.locationData,  // Spread the full location data first
        // Override with any specific fields we need to ensure
        title: business.locationData.title || business.name,
        storefrontAddress: business.locationData.storefrontAddress || {
          formattedAddress: business.address
        }
      } : {
        // Fallback if no locationData (shouldn't happen from Dashboard)
        name: `locations/${locationId}`,
        title: business.name,
        storefrontAddress: {
          formattedAddress: business.address
        }
      };

      logger.log('[BusinessTable] Created GMB objects:', {
        gmbAccount,
        gmbLocation,
        locationName: gmbLocation.name,
        accountName: gmbAccount.name
      });

      // Store in localStorage (for backward compatibility)
      localStorage.setItem('selectedGMBAccount', JSON.stringify(gmbAccount));
      localStorage.setItem('selectedGMBLocation', JSON.stringify(gmbLocation));

      // Create profile object for context
      const profileData = {
        id: locationId,
        locationId: locationId,
        accountId: accountId,
        name: business.name,
        address: business.address,
        locationData: gmbLocation,
        account: gmbAccount
      };

      // If trial-only user without full access, show trial landing instead of selecting
      try {
        if (!isAdmin && !hasFullAccess) {
          const token = window.localStorage.getItem('came_from_trial') || (document.cookie.match(/(?:^|; )came_from_trial=([^;]+)/) || [])[1];
          const sig = window.localStorage.getItem('came_from_trial_sig') || (document.cookie.match(/(?:^|; )came_from_trial_sig=([^;]+)/) || [])[1];
          if (token) {
            let qs = `trial=true&trialToken=${encodeURIComponent(token)}`;
            if (sig) qs += `&sig=${encodeURIComponent(sig)}`;
            navigate(`/trial-landing?${qs}`);
            return;
          }
        }
      } catch (e) { }

      // Update global context
      setDefaultProfile(profileData);

      // Verify what was stored
      const stored = {
        account: JSON.parse(localStorage.getItem('selectedGMBAccount')),
        location: JSON.parse(localStorage.getItem('selectedGMBLocation'))
      };

      logger.log('[BusinessTable] ✅ Stored in localStorage:', stored);
      logger.log('[BusinessTable] Extracted stored IDs:', {
        storedAccountId: stored.account.name?.split('/').pop(),
        storedLocationId: stored.location.name?.split('/').pop()
      });

      setSnackbarMessage(`${business.name} set as default profile`);
      setSnackbarOpen(true);

      logger.log('[BusinessTable] ✅ Default profile set globally:', {
        accountId,
        locationId,
        businessName: business.name
      });
    } catch (error) {
      logger.error('[BusinessTable] Error setting default profile:', error);
      setSnackbarMessage('Error setting default profile');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const getOptimizationDisplay = (business) => {
    const deriveLocId = (b) => {
      const direct = b?.locationId || b?.id;
      if (direct) return String(direct);
      if (typeof b?.name === 'string' && b.name.includes('/')) {
        const parts = String(b.name).split('/');
        return parts[parts.length - 1];
      }
      return '';
    };

    const businessLocId = deriveLocId(business);
    const savedLocId = profileSnapshot?.locationId ? String(profileSnapshot.locationId) : '';

    // First check if profileSnapshot has the score
    if (businessLocId && savedLocId && businessLocId === savedLocId && typeof profileSnapshot?.score === 'number') {
      return (
        <ScoreContainer>
          <OptimizationScore>
            {profileSnapshot.score} / 300
          </OptimizationScore>
          <Tooltip title="Refresh score" arrow>
            <RefreshIconButton onClick={() => handleRefreshScore(business)}>
              <RefreshRounded />
            </RefreshIconButton>
          </Tooltip>
        </ScoreContainer>
      );
    }

    // Then check if business has optimizationScore from database (passed from Dashboard)
    if (business.optimizationScore && business.optimizationScore !== 'Check now' && business.optimizationScore !== 'N/A') {
      return (
        <ScoreContainer>
          <OptimizationScore>
            {business.optimizationScore}
          </OptimizationScore>
          <Tooltip title="Refresh score" arrow>
            <RefreshIconButton onClick={() => handleRefreshScore(business)}>
              <RefreshRounded />
            </RefreshIconButton>
          </Tooltip>
        </ScoreContainer>
      );
    }

    return (
      <CheckNowButton onClick={() => handleCheckNow(business)}>
        Check Now
      </CheckNowButton>
    );
  };

  const getStatus = (business) => {
    const vs = (business.verificationState || business.verification_status || '').toUpperCase();
    let derived = business.status;
    const boolVerified =
      business.verified === true ||
      business?.locationState?.isVerified === true ||
      business?.metadata?.verified === true;
    if (!derived) {
      if (boolVerified || vs === 'VERIFIED') derived = 'verified';
      else if (
        vs === 'PENDING_VERIFICATION' ||
        vs === 'PENDING' ||
        vs === 'VERIFICATION_PENDING' ||
        vs === 'NEEDS_VERIFICATION'
      )
        derived = 'pending_verification';
      else if (vs === 'SUSPENDED') derived = 'suspended';
      else if (vs) derived = 'unverified';
    }
    return derived || (boolVerified ? 'verified' : 'unverified');
  };

  // Render mobile card view
  const renderMobileCard = (business, index) => {
    const finalStatus = getStatus(business);
    const isDefault = (business.locationId || business.id) === defaultProfileId;

    return (
      <MobileBusinessCard key={business.id || business._id || index}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <BusinessName>
                {business.name || business.businessName}
              </BusinessName>
              <BusinessAddress>
                {business.address || business.formattedAddress || '23 Maplewood Lane,IL 62704,USA'}
              </BusinessAddress>
            </Box>

            <Box>
              <LabelText>Status</LabelText>
              <StatusBadge status={finalStatus}>{finalStatus}</StatusBadge>
            </Box>

            <Box>
              <LabelText>Optimization Score</LabelText>
              {getOptimizationDisplay(business)}
            </Box>

            <Box>
              <LabelText>Paid / Plan</LabelText>
              {(() => {
                const locId = String(business.locationId || business.id || '');
                const sub = (subscriptions && subscriptions[locId]) || business.subscription || null;
                const plan = sub?.plan || sub?.product?.plan || business.plan || planByGmb[locId] || null;
                let dotColor = '#9CA3AF';
                if (sub) {
                  const expires = sub.expires_at ? new Date(sub.expires_at) : null;
                  if (sub.status === 'active' && expires && expires > new Date()) dotColor = '#10B981';
                  else if (expires && expires < new Date()) dotColor = '#EF4444';
                  else if (sub.status === 'active') dotColor = '#10B981';
                  else dotColor = '#EF4444';
                }

                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusDot color={dotColor} />
                    {plan ? (
                      <PlanChip plan={plan} />
                    ) : (
                      <Typography sx={{ fontSize: 12, color: '#64748B' }}>No Plan</Typography>
                    )}
                  </Box>
                );
              })()}
            </Box>

            <SelectDefaultButton
              onClick={() => handleSelectDefaultProfile(business)}
              fullWidth
              isdefault={isDefault ? 'true' : 'false'}
            >
              {isDefault && <CheckCircle sx={{ fontSize: 16 }} />}
              {isDefault ? 'Default Profile' : 'Set as Default'}
            </SelectDefaultButton>

            <ViewProfileButton
              onClick={() => handleViewProfile(business)}
              fullWidth
              variant="outlined"
            >
              View Profile
            </ViewProfileButton>
          </Stack>
        </CardContent>
      </MobileBusinessCard>
    );
  };

  return (
    <>
      {/* Desktop/Tablet Table View */}
      <StyledTableContainer component={Paper}>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell>Business</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Optimization score</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Default Profile</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {businesses.map((business, index) => {
              const finalStatus = getStatus(business);
              const isDefault = (business.locationId || business.id) === defaultProfileId;

              const deriveLocId = (b) => String(b.locationId || b.id || (typeof b.name === 'string' && b.name.includes('/') ? b.name.split('/').pop() : ''));
              const locId = deriveLocId(business);
              const sub = (subscriptions && subscriptions[locId]) || business.subscription || null;
              const plan = sub?.plan || sub?.product?.plan || business.plan || planByGmb[locId] || null;

              return (
                <StyledTableRow key={business.id || business._id || index}>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <BusinessName>
                        {business.name || business.businessName}
                      </BusinessName>
                      <BusinessAddress>
                        {business.address || business.formattedAddress || '23 Maplewood Lane,IL 62704,USA'}
                      </BusinessAddress>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={finalStatus}>{finalStatus}</StatusBadge>
                  </TableCell>
                  <TableCell>{getOptimizationDisplay(business)}</TableCell>
                  <TableCell>
                    <PlanCellBox>
                      {plan ? (
                        <PlanChip plan={plan} />
                      ) : (
                        <Typography sx={{ fontSize: 12, color: '#64748B' }}>No Plan</Typography>
                      )}
                    </PlanCellBox>
                  </TableCell>
                  <TableCell>
                    <SelectDefaultButton
                      onClick={() => handleSelectDefaultProfile(business)}
                      isdefault={isDefault ? 'true' : 'false'}
                    >
                      {isDefault && <CheckCircle sx={{ fontSize: 16 }} />}
                      {isDefault ? 'Default Profile' : 'Set as Default'}
                    </SelectDefaultButton>
                  </TableCell>
                  <TableCell>
                    <ViewProfileButton
                      onClick={() => handleViewProfile(business)}
                      data-tutorial={index === 0 ? "view-profile" : undefined}
                    >
                      View Profile
                    </ViewProfileButton>
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Mobile Card View */}
      <MobileCardsContainer>
        {businesses.map((business, index) => renderMobileCard(business, index))}
      </MobileCardsContainer>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BusinessTable;
